import { useCallback, useEffect, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";
import { useDebounce, useUpdateEffect } from "usehooks-ts";

import { faPlus } from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Modal from "components/Modal";
import useNotification from "components/Notification/useNotification";
import SearchInput from "components/SearchInput";
import ToolDropdownSelector from "components/ToolDropdownSelector";
import ToolRangeSelector from "components/ToolRangeSelector";
import useActivityTracker from "hooks/useActivityTracker";
import { ApiEndpoint } from "utils/baseFetcher";

import { useMaxZScore } from "./utils";

export const TYPES = [
  {
    value: "adr",
    label: "ADR",
    valueLabel: "ADR",
  },
  {
    value: "hc",
    label: "Holdco",
    valueLabel: "Holdco",
  },
  {
    value: "ah",
    label: "AH",
    valueLabel: "AH",
  },
];

const SCORES = [
  {
    value: "1",
    label: "1",
    valueLabel: "1",
  },
  {
    value: "2",
    label: "2",
    valueLabel: "2",
  },
  {
    value: "3",
    label: "3",
    valueLabel: "3",
  },
  {
    value: "4",
    label: "4",
    valueLabel: "4",
  },
  {
    value: "5",
    label: "5",
    valueLabel: "5",
  },
];

export const DEFAULT_FILTERS = {};

export default function Filters({ onChange }: any) {
  const { trackActivity } = useActivityTracker();

  const [filters, setFilters] = useState<any>(DEFAULT_FILTERS);
  const [addPair, setAddPair] = useState(false);
  const [primaryPair, setPrimaryPair] = useState<any>({});
  const [counterpartPair, setCounterpartPair] = useState<any>({});
  const { setNotification } = useNotification();

  const [trackingFilters, setTrackingFilters] = useState<any>({});

  const debouncedTrackingFilters = useDebounce<any>(trackingFilters, 5000);

  const { maxZScore } = useMaxZScore();

  const onAddPrimary = useCallback((item) => {
    setPrimaryPair(item);
  }, []);

  const onAddCounterpart = useCallback((item) => {
    setCounterpartPair(item);
  }, []);

  const onAddNewPair = useCallback(() => {
    trackActivity("relative_value_tool.requestpairclick", null, {
      primary: primaryPair?.id,
      secondary: counterpartPair?.id,
    });

    setAddPair(false);
    setNotification(
      "Pair addition request submitted successfully. You will be notified once it is added.",
      "success"
    );
  }, [counterpartPair, primaryPair, setNotification, trackActivity]);

  const emptyPrimary = useMemo(() => {
    return (
      <div className="flex flex-col justify-center items-center">
        <div className="py-[15px] text-center">No Data</div>
        <div
          onClick={() => setAddPair(true)}
          className="text-smart-teal py-[15px] pt-0 flex items-center gap-[10px] cursor-pointer hover:text-dark-teal"
        >
          <FontAwesomeIcon icon={faPlus} />
          Request Pair
        </div>
      </div>
    );
  }, []);

  const selection = useMemo(() => {
    return (
      <div className="">
        <label>Primary</label>
        <SearchInput
          inputClassName={(isFocusing) =>
            twMerge(
              isFocusing ? "" : "border-0",
              "pl-0 py-[20px] rounded-md placeholder:text-[13px]"
            )
          }
          className="w-full border-[1px] border-solid border-smart-teal rounded-md mb-[20px]"
          iconClassName={() => "text-shady-gray"}
          placeholder={primaryPair?.prettyName}
          resultPlaceholder="Search by name or Bloomberg Ticker"
          endpoint={"/api/v2/entities"}
          queryParam="filter[fulltext]"
          onItemSelected={onAddPrimary}
        />

        <label>Counterpart</label>
        <SearchInput
          inputClassName={(isFocusing) =>
            twMerge(
              isFocusing ? "" : "border-0",
              "pl-0 py-[20px] rounded-md placeholder:text-[13px]"
            )
          }
          className="w-full border-[1px] border-solid border-smart-teal rounded-md mb-[20px]"
          iconClassName={() => "text-shady-gray"}
          placeholder={counterpartPair?.prettyName}
          resultPlaceholder="Search by name or Bloomberg Ticker"
          endpoint={"/api/v2/entities"}
          queryParam="filter[fulltext]"
          onItemSelected={onAddCounterpart}
        />
        <div
          onClick={onAddNewPair}
          className="text-white  bg-smart-teal py-[10px] rounded-full flex items-center justify-center gap-[10px] cursor-pointer hover:bg-dark-teal"
        >
          <FontAwesomeIcon icon={faPlus} />
          Request Pair
        </div>
      </div>
    );
  }, [
    counterpartPair?.prettyName,
    onAddCounterpart,
    onAddNewPair,
    onAddPrimary,
    primaryPair?.prettyName,
  ]);

  const onSelectOption = useCallback(
    (selected, key) => {
      const selectedValue =
        selected.map?.((item: any) => item.value) || selected.value;
      const newFilters = {
        ...filters,
        [key]: selectedValue?.join?.(",") || selected.value,
      };
      setFilters(newFilters);
      setTrackingFilters({
        ...trackingFilters,
        ...newFilters,
      });
    },
    [filters, trackingFilters]
  );

  const onSelectRange = useCallback(
    (min, max, key) => {
      const newFilters = {
        ...filters,
        [key]: [min, max].join(","),
      };
      setFilters(newFilters);
      setTrackingFilters({
        ...trackingFilters,
        ...newFilters,
      });
    },
    [filters, trackingFilters]
  );

  useEffect(() => {
    onChange?.(filters);
  }, [filters, onChange]);

  useUpdateEffect(() => {
    trackActivity("relative_value.filters_change", undefined, {
      ...debouncedTrackingFilters,
    });
  }, [debouncedTrackingFilters]);

  return (
    <div className="flex flex-col relative">
      <div className="flex p-[4px] gap-2 mb-[10px] overflow-auto">
        <ToolDropdownSelector
          label="Primary"
          endpoint={(pageIndex, filter) => {
            const endpoint = new ApiEndpoint("/api/v2/entities");
            endpoint.searchParams.append("page[number]", pageIndex.toString());
            endpoint.searchParams.append("sort", "pretty-name");
            endpoint.searchParams.append(
              "fields[entities]",
              "slug,pretty-name"
            );

            endpoint.searchParams.append(
              "filter[from-relative-value-pair-primary]",
              "true"
            );

            if (filter)
              endpoint.searchParams.append("filter[fulltext]", filter);

            return endpoint.href;
          }}
          dataTransform={(item: any) => ({
            value: item.id,
            label: item.prettyName,
          })}
          multiple
          filterKey="entity-x-id"
          filters={filters}
          onSelect={(selected) => onSelectOption(selected, "entity-x-id")}
          dropdownClassName="w-[300px]"
          narrow
          containerClassName="w-auto flex-1"
          emptyState={emptyPrimary}
        />
        <ToolDropdownSelector
          label="Counterpart"
          endpoint={(pageIndex, filter) => {
            const endpoint = new ApiEndpoint("/api/v2/entities");
            endpoint.searchParams.append("page[number]", pageIndex.toString());
            endpoint.searchParams.append("sort", "pretty-name");
            endpoint.searchParams.append(
              "fields[entities]",
              "slug,pretty-name"
            );

            endpoint.searchParams.append(
              "filter[from-relative-value-pair-counter-part]",
              "true"
            );

            if (filter)
              endpoint.searchParams.append("filter[fulltext]", filter);

            return endpoint.href;
          }}
          dataTransform={(item: any) => ({
            value: item.id,
            label: item.prettyName,
          })}
          multiple
          filterKey="entity-y-id"
          filters={filters}
          onSelect={(selected) => onSelectOption(selected, "entity-y-id")}
          dropdownClassName="w-[300px]"
          narrow
          containerClassName="w-auto flex-1"
        />
        <ToolDropdownSelector
          label="Type"
          options={TYPES}
          selected={TYPES}
          multiple
          filterKey="pair-type"
          filters={filters}
          onSelect={(selected) => onSelectOption(selected, "pair-type")}
          dropdownClassName="w-[300px]"
          narrow
          containerClassName="w-auto flex-1"
        />
        <ToolDropdownSelector
          label="SK Actionability Score"
          options={SCORES}
          selected={SCORES}
          multiple
          filterKey="sk-actionability-score"
          onSelect={(selected) =>
            onSelectOption(selected, "sk-actionability-score")
          }
          filters={filters}
          narrow
          containerClassName="w-auto flex-1"
        />
        <ToolDropdownSelector
          label="SK Investability Score"
          options={SCORES}
          selected={SCORES}
          multiple
          filterKey="sk-liquidity-score"
          filters={filters}
          onSelect={(selected) =>
            onSelectOption(selected, "sk-liquidity-score")
          }
          narrow
          containerClassName="w-auto flex-1"
        />
        <ToolRangeSelector
          label="xSD 1 Yr Mean"
          min={0}
          max={maxZScore || 13}
          step={0.1}
          filterKey="z-score-abs"
          onValueChange={onSelectRange}
          filters={filters}
          narrow
          showInput
          containerClassName="w-auto flex-1"
        />
        {Object.keys(filters).length > 0 && (
          <div>
            <div className="p-[4px] flex min-w-[150px]">
              <Button
                onClick={() => setFilters({})}
                className="!bg-smart-teal !ml-0 !mr-0 w-full"
              >
                Clear All Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {addPair && (
        <Modal
          className={twMerge("md:w-[550px] md:h-[300px]")}
          overlayClassName="bg-black/70 flex flex-col md:justify-center"
          onClose={() => setAddPair(false)}
          onAfterOpen={() => {
            document.body.classList.add("!max-h-[calc(100vh-100px)]");
          }}
          onAfterClose={() => {
            document.body.classList.remove("!max-h-[calc(100vh-100px)]");
            setAddPair(false);
          }}
        >
          <div className="p-[30px]">
            <h1 className="text-[18px] font-medium">
              Add Primary and Counterpart Pair
            </h1>
            <div className="flex flex-col gap-[15px]">{selection}</div>
          </div>
        </Modal>
      )}
    </div>
  );
}
